import React, { useState, useRef, useEffect } from 'react';
import {
  Form, Card, CardBody,
} from 'reactstrap';
import { friendlyErrorMessage } from 'app/utils/parsers';
import Button from 'app/components/general/Button';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import Alert from 'app/components/general/Alert';
import _ from 'lodash';
import { Link } from 'react-router-dom';

import EmailIcon from 'app/images/icons/email.svg';
import PasswordIcon from 'app/images/icons/password.svg';

import IActions from 'javascript/types/actions';
import validateInput from './LoginFormValidator';

interface IProps {
  actions?: IActions;
  email: string;
  password: string;
  isLoading?: boolean;
  hasLoaded?: boolean;
  hasFailed?: boolean;
  isLoggedIn?: boolean;
  changePasswordSuccess: boolean;
  backendErrors: any;
  match: any;
}

interface ILoginFormState {
  email?: string;
  password?: string;
  backendErrors?: any;
  errors: any;
  changed: boolean;
  isValid: boolean;
}
const LoginForm: FC<IProps> = (props: IProps = {
  email: '',
  password: '',
  backendErrors: {},
  changePasswordSuccess: false,
  match: {},
}) => {
  const [changed, setChanged] = useState(false);
  const [savedIsValid, setSavedIsValid] = useState(true);
  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState(null);
  const [errors, setErrors] = useState({});

  const emailField = useRef(null);
  const passwordField = useRef(null);

  useEffect(() => {
    props.actions.resetAuthenticateErrors();
  }, []);

  useEffect(() => {
    let emailCount = 0;
    let passwordCount = 0;
    const emailInterval = setInterval(() => {
      emailCount += 1;
      if (emailField.current) {
        setEmail(emailField.current.value);
        clearInterval(emailInterval);
      }
      // if (emailCount >= 10) {
      //   clearInterval(emailInterval);
      // }
    }, 100);
    const passwordInterval = setInterval(() => {
      passwordCount += 1;

      if (passwordField.current) {
        setPassword(passwordField.current.value);
        clearInterval(passwordInterval);
      }
      // if (passwordCount >= 10) {
      //   clearInterval(passwordInterval);
      // }
    }, 100);
  });

  const isValid = (): boolean => {
    const { checkErrors, checkIsValid } = validateInput({ email, password });
    if (!checkIsValid) {
      setErrors(checkErrors);
      setSavedIsValid(false);
    } else {
      setErrors({});
      setSavedIsValid(true);
    }

    return checkIsValid;
  };

  const handleEmailChange = (e): void => {
    setEmail(e.target.value);
    setErrors({
      ...errors,
      email: undefined,
    });
    setChanged(true);
  };
  const handlePasswordChange = (e): void => {
    setPassword(e.target.value);
    setErrors({
      ...errors,
      password: undefined,
    });
    setChanged(true);
  };

  const renderBackendErrors = (): React.ReactNode => {
    if (
      !_.isEmpty(props.backendErrors?.message)
    ) {
      return (
        <Alert color="error">
          {friendlyErrorMessage(props.backendErrors.message)}
        </Alert>
      );
    }
    return null;
  };

  const handleSubmit = (e): void => {
    e.preventDefault();
    let redirectLocation;

    if (isValid()) {
      setChanged(false);
      if (props.match?.params?.provider === 'gameday') {
        redirectLocation = '/create-connection/gameday';
      }
      props.actions.authenticateCognitoUser({
        email: _.trim(_.toLower(email)),
        password,
      }, true, redirectLocation);
    }
  };

  return (
    <div>
      <div className="row">
        <div className="col">
          <Card className="extra-shadow">
            <CardBody className="with-extra-padding-2">
              <div className="row">
                <div className="col">
                  <h2>Sign in to your organisation</h2>
                </div>
              </div>
              {props.changePasswordSuccess && (
                <div className="row">
                  <div className="col">
                    <Alert color="success">Password changed successfully, please login with your new password.</Alert>
                  </div>
                </div>
              )}
              {renderBackendErrors()}
              <div className="row">
                <div className="col with-padding">
                  <Form autoComplete="off" onSubmit={handleSubmit}>
                    <InputGroupBlock
                      type="text"
                      icon={EmailIcon}
                      labelText="Your email"
                      placeholder="Your email"
                      aria-label="Your email"
                      errors={errors}
                      autoComplete="on"
                      inputGroupClassName="large"
                      tabIndex="1"
                      name="email"
                      value={email}
                      innerRef={emailField}
                      onChange={handleEmailChange}
                      onBlur={handleEmailChange}
                    />
                    <InputGroupBlock
                      type="password"
                      icon={PasswordIcon}
                      labelText="Password"
                      placeholder="Password"
                      aria-label="Password"
                      errors={errors}
                      autoComplete="on"
                      inputGroupClassName="large"
                      hint={(
                        <Link to="/forgot-password">
                          Forgot your password?
                        </Link>
                      )}
                      tabIndex="2"
                      name="password"
                      innerRef={passwordField}
                      value={password}
                      onChange={handlePasswordChange}
                      onBlur={handlePasswordChange}
                    />
                    <Button
                      color="primary"
                      className="btn-1 btn-full-width large"
                      tabIndex="3"
                      loadingText="Logging in"
                      delaySubmit
                      // disabled={!isValid}
                      isLoading={props.isLoading}
                      hasSaved={props.hasLoaded}
                      hasFailed={props.hasFailed || !savedIsValid}
                      type="submit"
                    >
                      Log in
                    </Button>
                  </Form>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
      <div className="row justify-content-center sign-up">
        <div className="col text-center with-margin-bottom-10">
          <span className="d-inline-block text-grey">Don't have an account?</span>
          {' '}
          <Link to="/get-started">Sign up</Link>
        </div>
      </div>
    </div>
  );
};
export default LoginForm;
